import type { ContractServiceConfigOption } from '@/api/contracts';

type Opt = ContractServiceConfigOption & { title?: string };

/**
 * One-line config glance for a VCloud product, shown in the product-card header
 * so sales/CS staff can scan what each product is configured as WITHOUT
 * expanding — e.g. "Predefined · 2 VMs" or "Custom · 1 VM, 1 LB".
 *
 * Read-only over `service_configoptions`; returns null when there's nothing
 * meaningful to show yet (so the caller can fall back to the line amount).
 */
export function vcloudConfigHint(options: Opt[] | undefined): string | null {
  if (!Array.isArray(options) || options.length === 0) return null;

  const vms = options.filter((o) => (o.title ?? 'vm-instance') === 'vm-instance').length;
  const lbs = options.filter((o) => o.title === 'vm-lb').length;
  const others = options.filter((o) => o.title === 'vm-other').length;

  // Mode comes from the VM rows: a custom row carries unit:'custom'.
  const isCustom = options.some((o) => o.unit === 'custom');
  const mode = isCustom ? 'Custom' : 'Predefined';

  const parts: string[] = [];
  if (vms > 0) parts.push(`${vms} VM${vms === 1 ? '' : 's'}`);
  if (lbs > 0) parts.push(`${lbs} LB${lbs === 1 ? '' : 's'}`);
  if (others > 0) parts.push(`${others} add-on${others === 1 ? '' : 's'}`);

  if (parts.length === 0) return mode;
  return `${mode} · ${parts.join(', ')}`;
}
